import { useState, useEffect, useRef, forwardRef } from "react";
import HTMLFlipBook from "react-pageflip";
import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/firestore";
import { uploadPhoto, deletePhoto } from "../firebase/storage";
import "./TripBook.scss";

// ── helpers ───────────────────────────────────────────────────────────────────

function getDays(startDate, endDate) {
  const days = [];
  const start = new Date(startDate + "T00:00:00");
  const end = endDate ? new Date(endDate + "T00:00:00") : start;
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    days.push(`${y}-${m}-${day}`);
  }
  return days;
}

const fmtTime = (h, m) => {
  const period = h >= 12 ? "PM" : "AM";
  const hr = h % 12 === 0 ? 12 : h % 12;
  return `${hr}:${String(m).padStart(2, "0")} ${period}`;
};
const polaroidRot = (i) => [-4, 2.5, -2, 3.5, -3, 1.5, 4, -1.5][i % 8];

function sortEntries(entries) {
  const timed = entries
    .filter((e) => e.hour != null)
    .sort(
      (a, b) => a.hour * 60 + (a.minute ?? 0) - (b.hour * 60 + (b.minute ?? 0)),
    );
  const timeless = entries.filter((e) => e.hour == null);
  return [...timed, ...timeless];
}

// Rough height estimate for a single entry (px)
function estimateEntryHeight(entry) {
  let h = 42; // time badge + entry-block padding + gap
  if (entry.description?.trim())
    h += Math.max(24, Math.ceil(entry.description.length / 42) * 22 + 8);
  if (entry.photos?.length > 0) {
    // Each polaroid is 100px wide + ~10px gap = ~110px; page content ~376px wide ≈ 3 per row.
    // Per row: 90px img + 5px top + 20px bottom padding = 115px + ~10px row-gap = 125px.
    const rows = Math.ceil(entry.photos.length / 3);
    h += rows * 125 + 14; // 14px = strip top/bottom padding
  }
  return h;
}

// PAGE_BUDGET is computed dynamically from the rendered container — see usable height below.
// This constant is only the fallback for the initial render before ResizeObserver fires.
const DEFAULT_PAGE_BUDGET = 520;

// Returns array of { date, slotEntries, slotIdx, isTemplate, templateData }
// Min 3 day-slots; extends to cover last day with content + 1 buffer
// Days with many entries spill into multiple consecutive slots
function buildPageSlots(allDays, entries, budget = DEFAULT_PAGE_BUDGET) {
  const templateEntries = entries.filter((e) => e.entryType);
  const regularEntries = entries.filter((e) => !e.entryType);

  const byDate = {},
    templatesByDate = {};
  allDays.forEach((d) => {
    byDate[d] = [];
    templatesByDate[d] = [];
  });
  regularEntries.forEach((e) => {
    if (byDate[e.date]) byDate[e.date].push(e);
  });
  templateEntries.forEach((e) => {
    if (templatesByDate[e.date]) templatesByDate[e.date].push(e);
  });

  const filled = new Set(entries.map((e) => e.date));
  let lastIdx = -1;
  allDays.forEach((d, i) => {
    if (filled.has(d)) lastIdx = i;
  });
  const daysToShow = allDays.slice(
    0,
    Math.min(allDays.length, Math.max(3, lastIdx + 2)),
  );

  const slots = [];
  daysToShow.forEach((date) => {
    // Template pages first for this date (each is its own full-page slot)
    templatesByDate[date].forEach((tmpl) => {
      slots.push({
        date,
        slotEntries: [],
        slotIdx: 0,
        isTemplate: true,
        templateData: tmpl,
      });
    });

    // Regular entry slots
    const dayEntries = sortEntries(byDate[date]);
    if (dayEntries.length === 0) {
      slots.push({ date, slotEntries: [], slotIdx: 0, isTemplate: false });
      return;
    }
    let batch = [],
      used = 0,
      slotIdx = 0;
    for (const entry of dayEntries) {
      const h = estimateEntryHeight(entry);
      if (batch.length > 0 && used + h > budget) {
        slots.push({ date, slotEntries: batch, slotIdx, isTemplate: false });
        slotIdx++;
        batch = [entry];
        used = h;
      } else {
        batch.push(entry);
        used += h;
      }
    }
    slots.push({ date, slotEntries: batch, slotIdx, isTemplate: false });
  });

  return slots;
}

const fmtDateHeader = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

// ── Template 1 ────────────────────────────────────────────────────────────────

const TEMPLATE_COLORS = [
  "#FF6B6B",
  "#e91e63",
  "#FF8E53",
  "#fce138",
  "#C8F06C",
  "#6DD9A8",
  "#4DD0E1",
  "#42A5F5",
  "#5C6BC0",
  "#AB47BC",
  "#FFFFFF",
  "#E8E0D5",
  "#C4A882",
  "#8B5E3C",
  "#3D1F0F",
  "#B71C1C",
  "#1B5E20",
  "#0D47A1",
  "#4A148C",
  "#F57F17",
];

// Scale paragraph font down for longer text
const t1FontSize = (text) =>
  `${Math.max(0.62, 0.9 - ((text?.length ?? 0) / 500) * 0.28)}rem`;

// Shared template content renderer — used by both Template1Page and Template2Page
const TemplateContent = ({ data, tc, dividerColor, contentClass }) => (
  <div className={contentClass}>
    {data.coloredText1 && (
      <div className="t1-label" style={{ color: data.color1 || "#FF8E53" }}>
        {data.coloredText1}
      </div>
    )}
    {data.text2 && (
      <div className="t1-header" style={{ color: tc }}>
        {data.text2}
      </div>
    )}
    {data.text3 && (
      <p
        className="t1-paragraph"
        style={{ fontSize: t1FontSize(data.text3), color: tc }}
      >
        {data.text3}
      </p>
    )}
    {(data.text4 || data.text5) && (
      <div className="t1-credit" style={{ color: tc }}>
        {data.text4}
        {data.text4 && data.text5 ? " • " : ""}
        {data.text5}
      </div>
    )}
    {(data.text4 || data.text5) && (data.text6 || data.text8) && (
      <div className="t1-divider" style={{ background: dividerColor }} />
    )}
    {data.text6 && (
      <div className="t1-social-row">
        <span className="t1-social" style={{ color: tc }}>
          {data.text6}
        </span>
      </div>
    )}
    {data.text8 && (
      <div className="t1-social-row">
        <span className="t1-social" style={{ color: tc }}>
          {data.text8}
        </span>
      </div>
    )}
  </div>
);

const Template1Page = forwardRef(({ data, tripId, editMode, onEdit }, ref) => {
  const remove = async () => {
    if (data.imagePath) await deletePhoto(data.imagePath);
    await deleteDoc(doc(db, "trips", tripId, "dayEntries", data.id));
  };
  const tc = data.textColor || "#1a0f08";
  const dividerColor =
    tc === "#ffffff" ? "rgba(255,255,255,0.35)" : "rgba(139,94,60,0.25)";

  return (
    <div className="journal-page template1-page" ref={ref}>
      {data.imageUrl ? (
        <div
          className="t1-image"
          style={{ backgroundImage: `url("${data.imageUrl}")` }}
        />
      ) : (
        <div className="t1-image t1-image--empty" />
      )}
      {editMode && (
        <div className="t1-edit-actions">
          <button className="t1-action-btn" onClick={onEdit} title="Edit page">
            ✏️
          </button>
          <button
            className="t1-action-btn t1-action-btn--del"
            onClick={remove}
            title="Delete page"
          >
            ×
          </button>
        </div>
      )}
      <TemplateContent
        data={data}
        tc={tc}
        dividerColor={dividerColor}
        contentClass="t1-content"
      />
    </div>
  );
});
Template1Page.displayName = "Template1Page";

// ── Template 2 (full-bleed, content top-left) ─────────────────────────────────

const Template2Page = forwardRef(({ data, tripId, editMode, onEdit }, ref) => {
  const remove = async () => {
    if (data.imagePath) await deletePhoto(data.imagePath);
    await deleteDoc(doc(db, "trips", tripId, "dayEntries", data.id));
  };
  const tc = data.textColor || "#ffffff";
  const dividerColor =
    tc === "#ffffff" ? "rgba(255,255,255,0.35)" : "rgba(60,30,10,0.22)";

  return (
    <div className="journal-page template2-page" ref={ref}>
      {data.imageUrl ? (
        <div
          className="t2-image"
          style={{ backgroundImage: `url("${data.imageUrl}")` }}
        />
      ) : (
        <div className="t2-image t2-image--empty" />
      )}
      {editMode && (
        <div className="t1-edit-actions">
          <button className="t1-action-btn" onClick={onEdit} title="Edit page">
            ✏️
          </button>
          <button
            className="t1-action-btn t1-action-btn--del"
            onClick={remove}
            title="Delete page"
          >
            ×
          </button>
        </div>
      )}
      <TemplateContent
        data={data}
        tc={tc}
        dividerColor={dividerColor}
        contentClass="t2-content"
      />
    </div>
  );
});
Template2Page.displayName = "Template2Page";

// ── Template 3 (2×2 image grid) ───────────────────────────────────────────────

const Template3Page = forwardRef(({ data, tripId, editMode, onEdit }, ref) => {
  const remove = async () => {
    for (let n = 1; n <= 4; n++) {
      if (data[`image${n}Path`]) await deletePhoto(data[`image${n}Path`]);
    }
    await deleteDoc(doc(db, "trips", tripId, "dayEntries", data.id));
  };

  return (
    <div className="journal-page template3-page" ref={ref}>
      {editMode && (
        <div className="t1-edit-actions">
          <button className="t1-action-btn" onClick={onEdit} title="Edit page">
            ✏️
          </button>
          <button
            className="t1-action-btn t1-action-btn--del"
            onClick={remove}
            title="Delete page"
          >
            ×
          </button>
        </div>
      )}
      <div className="t3-grid">
        {[1, 2, 3, 4].map((n) => (
          <div key={n} className="t3-cell">
            <div className="t3-text">
              {data[`label${n}`] && (
                <div
                  className="t3-label"
                  style={{ color: data[`color${n}`] || "#FF8E53" }}
                >
                  {data[`label${n}`]}
                </div>
              )}
              {data[`para${n}`] && (
                <p className="t3-para">{data[`para${n}`]}</p>
              )}
            </div>
            <div className="t3-img-wrap">
              <div
                className={`t3-img${!data[`image${n}Url`] ? " t3-img--empty" : ""}`}
                style={
                  data[`image${n}Url`]
                    ? { backgroundImage: `url("${data[`image${n}Url`]}")` }
                    : {}
                }
              />
            </div>
            <span
              className="t3-num"
              style={{ color: data[`numColor${n}`] || "#ffffff" }}
            >
              {n}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
});
Template3Page.displayName = "Template3Page";

// ── Template 4 (magazine 2-column) ────────────────────────────────────────────

const Template4Page = forwardRef(({ data, tripId, editMode, onEdit }, ref) => {
  const remove = async () => {
    for (const n of [1, 2, 3]) {
      if (data[`t4img${n}Path`]) await deletePhoto(data[`t4img${n}Path`]);
    }
    await deleteDoc(doc(db, "trips", tripId, "dayEntries", data.id));
  };

  const setCount = data.t4setCount || 1;

  return (
    <div className="journal-page template4-page" ref={ref}>
      {editMode && (
        <div className="t1-edit-actions">
          <button className="t1-action-btn" onClick={onEdit} title="Edit page">
            ✏️
          </button>
          <button
            className="t1-action-btn t1-action-btn--del"
            onClick={remove}
            title="Delete page"
          >
            ×
          </button>
        </div>
      )}
      <div className="t4-layout">
        {/* Left column */}
        <div className="t4-left">
          <div
            className={`t4-img1${!data.t4img1Url ? " t4-img--empty" : ""}`}
            style={
              data.t4img1Url
                ? { backgroundImage: `url("${data.t4img1Url}")` }
                : {}
            }
          />
          <div className="t4-text-block">
            {data.t4text1 && (
              <div
                className="t4-label"
                style={{ color: data.t4text1Color || "#FF8E53" }}
              >
                <span>{data.t4text1}</span>
              </div>
            )}
            {data.t4text2 && <div className="t4-title">{data.t4text2}</div>}
            {data.t4text3 && <div className="t4-subtitle">{data.t4text3}</div>}
            {data.t4text4 && <p className="t4-body">{data.t4text4}</p>}
          </div>
          <div className="t4-img2-wrap">
            <div
              className={`t4-img2${!data.t4img2Url ? " t4-img--empty" : ""}`}
              style={
                data.t4img2Url
                  ? { backgroundImage: `url("${data.t4img2Url}")` }
                  : {}
              }
            />
            {data.t4text5 && <div className="t4-caption">{data.t4text5}</div>}
          </div>
        </div>
        {/* Right column */}
        <div className="t4-right">
          <div
            className={`t4-img3${!data.t4img3Url ? " t4-img--empty" : ""}`}
            style={
              data.t4img3Url
                ? { backgroundImage: `url("${data.t4img3Url}")` }
                : {}
            }
          />
          {data.t4text6 && (
            <div className="t4-section-header">{data.t4text6}</div>
          )}
          <div className="t4-sets">
            {Array.from({ length: setCount }, (_, i) => i + 1).map((n) => (
              <div key={n} className="t4-set">
                {data[`t4set${n}Title`] && (
                  <div className="t4-set-title">{data[`t4set${n}Title`]}</div>
                )}
                {data[`t4set${n}Para`] && (
                  <p className="t4-set-para">{data[`t4set${n}Para`]}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
});
Template4Page.displayName = "Template4Page";

// ── Template 5 (magazine article style) ───────────────────────────────────────

const Template5Page = forwardRef(({ data, tripId, editMode, onEdit }, ref) => {
  const remove = async () => {
    for (const n of [1, 2, 3]) {
      if (data[`t5img${n}Path`]) await deletePhoto(data[`t5img${n}Path`]);
    }
    await deleteDoc(doc(db, "trips", tripId, "dayEntries", data.id));
  };
  const setCount = data.t5setCount || 1;
  return (
    <div className="journal-page template5-page" ref={ref}>
      {editMode && (
        <div className="t1-edit-actions">
          <button className="t1-action-btn" onClick={onEdit} title="Edit page">
            ✏️
          </button>
          <button
            className="t1-action-btn t1-action-btn--del"
            onClick={remove}
            title="Delete page"
          >
            ×
          </button>
        </div>
      )}
      <div className="t5-layout">
        {/* Left column */}
        <div className="t5-left">
          <div
            className={`t5-img1${!data.t5img1Url ? " t5-img--empty" : ""}`}
            style={
              data.t5img1Url
                ? { backgroundImage: `url("${data.t5img1Url}")` }
                : {}
            }
          />
          <div className="t5-center-block">
            {data.t5coloredText1 && (
              <div
                className="t5-colored-label"
                style={{ color: data.t5coloredText1Color || "#e91e63" }}
              >
                {data.t5coloredText1}
              </div>
            )}
            {data.t5text1 && <div className="t5-title">{data.t5text1}</div>}
            {data.t5text2 && <div className="t5-subtitle">{data.t5text2}</div>}
          </div>
          <div className="t5-body-wrap">
            <div className="t5-body">
              <div
                className={`t5-img2${!data.t5img2Url ? " t5-img--empty" : ""}`}
                style={data.t5img2Url ? { backgroundImage: `url("${data.t5img2Url}")` } : {}}
              />
              {data.t5text3}
            </div>
          </div>
          {data.t5text9 && <div className="t5-img-caption">{data.t5text9}</div>}
        </div>
        {/* Right column */}
        <div className="t5-right">
          {data.t5text4 && (
            <div className="t5-section-header">{data.t5text4}</div>
          )}
          <div className="t5-sets">
            {Array.from({ length: setCount }, (_, i) => i + 1).map((n) => (
              <div key={n} className="t5-set">
                {data[`t5set${n}Text5`] && (
                  <div className="t5-highlight-label">
                    <span style={{ backgroundColor: data.t5highlightColor || "#fce138", color: "#1a0f08" }}>
                      {data[`t5set${n}Text5`]}
                    </span>
                  </div>
                )}
                {data[`t5set${n}Text6`] && (
                  <p className="t5-set-content">{data[`t5set${n}Text6`]}</p>
                )}
              </div>
            ))}
          </div>
          <div className="t5-callout-row">
            <div className="t5-callout-text">
              {data.t5text7 && <div className="t5-callout-title">{data.t5text7}</div>}
              {data.t5text8 && <p className="t5-callout-body">{data.t5text8}</p>}
            </div>
            <div className="t5-img3-wrap">
              {(data.t5circleTitle || data.t5circleBody) && (
                <div className="t5-circle" style={{ background: data.t5circleColor || "#fce138" }}>
                  {data.t5circleTitle && <div className="t5-circle-title">{data.t5circleTitle}</div>}
                  {data.t5circleBody && <div className="t5-circle-body">{data.t5circleBody}</div>}
                </div>
              )}
              <div
                className={`t5-img3${!data.t5img3Url ? " t5-img--empty" : ""}`}
                style={data.t5img3Url ? { backgroundImage: `url("${data.t5img3Url}")` } : {}}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});
Template5Page.displayName = "Template5Page";

// ── Template 6 — Collage ──────────────────────────────────────────────────────

const Template6Page = forwardRef(({ data, tripId, editMode, onEdit }, ref) => {
  const remove = async () => {
    for (let i = 1; i <= (data.t6imgCount || 1); i++) {
      if (data[`t6img${i}Path`]) await deletePhoto(data[`t6img${i}Path`]);
    }
    await deleteDoc(doc(db, "trips", tripId, "dayEntries", data.id));
  };

  const count = data.t6imgCount || 1;
  const layout = data.t6layout || "h";

  const imgs = Array.from({ length: count }, (_, i) => ({
    url: data[`t6img${i + 1}Url`] || "",
  }));

  return (
    <div className="journal-page template6-page" ref={ref}>
      {editMode && (
        <div className="t1-edit-actions">
          <button className="t1-action-btn" onClick={onEdit} title="Edit page">
            ✏️
          </button>
          <button
            className="t1-action-btn t1-action-btn--del"
            onClick={remove}
            title="Delete page"
          >
            ×
          </button>
        </div>
      )}
      <div className={`t6-grid t6-grid--n${count} ${count === 2 ? `t6-grid--${layout}` : ""}`}>
        {imgs.map((img, i) =>
          img.url ? (
            <div
              key={i}
              className="t6-cell"
              style={{ backgroundImage: `url(${img.url})` }}
            />
          ) : (
            <div key={i} className="t6-cell t6-cell--empty" />
          ),
        )}
      </div>
    </div>
  );
});
Template6Page.displayName = "Template6Page";

// ── Entry block ───────────────────────────────────────────────────────────────

const EntryBlock = ({ entry, tripId, onPhotoClick, editMode }) => {
  const [uploading, setUploading] = useState(false);
  const [editingDesc, setEditingDesc] = useState(entry.description ?? "");
  const fileRef = useRef(null);
  const entryRef = doc(db, "trips", tripId, "dayEntries", entry.id);

  const saveDesc = async (val) => {
    if (val.trim() !== (entry.description ?? "").trim())
      await updateDoc(entryRef, { description: val.trim() });
  };

  const addPhotos = async (files) => {
    setUploading(true);
    const newPhotos = [...(entry.photos ?? [])];
    for (const file of files) {
      const { url, path } = await uploadPhoto(file, `tripEntries/${tripId}`);
      newPhotos.push({ url, path });
    }
    await updateDoc(entryRef, { photos: newPhotos });
    setUploading(false);
  };

  const removePhoto = async (photo) => {
    const photos = (entry.photos ?? []).filter((p) => p.path !== photo.path);
    await updateDoc(entryRef, { photos });
    if (photo.path) await deletePhoto(photo.path);
  };

  const remove = async () => {
    for (const p of entry.photos ?? []) if (p.path) await deletePhoto(p.path);
    await deleteDoc(entryRef);
  };

  return (
    <div className={`entry-block ${editMode ? "entry-block--edit" : ""}`}>
      <div className="entry-top">
        {entry.hour != null && (
          <span className="entry-time">
            {fmtTime(entry.hour, entry.minute ?? 0)}
          </span>
        )}
        {editMode && (
          <button className="entry-del" onClick={remove} title="Delete entry">
            ×
          </button>
        )}
      </div>

      {editMode ? (
        <textarea
          className="entry-desc entry-desc--edit"
          value={editingDesc}
          onChange={(e) => setEditingDesc(e.target.value)}
          onBlur={(e) => saveDesc(e.target.value)}
          rows={2}
        />
      ) : (
        entry.description && <p className="entry-desc">{entry.description}</p>
      )}

      {entry.photos?.length > 0 && (
        <div className="polaroid-strip">
          {entry.photos.map((p, i) => (
            <div
              key={p.path || i}
              className="polaroid"
              style={{ "--rot": `${polaroidRot(i)}deg` }}
              onClick={() => onPhotoClick(p)}
            >
              <img src={p.url} alt="" draggable={false} />
              {editMode && (
                <button
                  className="polaroid-del"
                  onClick={(e) => {
                    e.stopPropagation();
                    removePhoto(p);
                  }}
                >
                  ×
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {editMode && (
        <>
          <button
            className="btn-add-photo-inline"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
          >
            {uploading ? "…" : "📷 add photo"}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={async (e) => {
              await addPhotos(Array.from(e.target.files));
              e.target.value = "";
            }}
          />
        </>
      )}
    </div>
  );
};

// ── Add-entry form (lives in popup outside the book) ──────────────────────────

const fmtDay = (iso, idx) => {
  const d = new Date(iso + "T00:00:00");
  const label = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  return `Day ${idx + 1} · ${label}`;
};

const AddEntryForm = ({ tripId, date, allDays, onDone }) => {
  const [time, setTime] = useState("");
  const [desc, setDesc] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedDay, setSelectedDay] = useState(date);
  const fileRef = useRef(null);

  const isMultiDay = allDays.length > 1;
  const effectiveDay = selectedDay || allDays[0];

  const save = async () => {
    setSaving(true);
    let photos = [];
    for (const file of files) {
      const { url, path } = await uploadPhoto(file, `tripEntries/${tripId}`);
      photos.push({ url, path });
    }
    const data = {
      date: effectiveDay,
      description: desc.trim(),
      photos,
      createdAt: serverTimestamp(),
    };
    if (time) {
      const [h, m] = time.split(":").map(Number);
      data.hour = h;
      data.minute = m;
    }
    await addDoc(collection(db, "trips", tripId, "dayEntries"), data);
    setSaving(false);
    onDone();
  };

  return (
    <div className="add-entry-form">
      {isMultiDay && (
        <select
          className="aef-day"
          value={selectedDay}
          onChange={(e) => setSelectedDay(e.target.value)}
        >
          <option value="">Day 1 (default)</option>
          {allDays.map((d, i) => (
            <option key={d} value={d}>
              {fmtDay(d, i)}
            </option>
          ))}
        </select>
      )}
      <div className="aef-row">
        <input
          type="time"
          className="aef-time"
          value={time}
          onChange={(e) => setTime(e.target.value)}
        />
        <button className="aef-photo" onClick={() => fileRef.current?.click()}>
          📷 {files.length > 0 ? files.length : ""}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) =>
            setFiles((prev) => [...prev, ...Array.from(e.target.files)])
          }
        />
      </div>
      <textarea
        className="aef-text"
        placeholder="What happened? ✍️"
        value={desc}
        onChange={(e) => setDesc(e.target.value)}
        rows={3}
        autoFocus
      />
      {files.length > 0 && (
        <div className="aef-files">
          {files.map((f, i) => (
            <span key={i} className="aef-file-tag">
              {f.name.slice(0, 18)}
              <button
                onClick={() => setFiles((fs) => fs.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <button
        className="aef-save"
        onClick={save}
        disabled={saving || (!desc.trim() && files.length === 0)}
      >
        {saving ? "Saving…" : "Add to page ♥"}
      </button>
    </div>
  );
};

const AddTemplate1Form = ({
  tripId,
  allDays,
  defaultDate,
  existing,
  onDone,
}) => {
  const [templateType, setTemplateType] = useState(
    existing?.entryType || "template1",
  );
  const [textColor, setTextColor] = useState(existing?.textColor || "#ffffff");
  const [date, setDate] = useState(existing?.date || defaultDate || allDays[0]);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(existing?.imageUrl || "");
  const [color1, setColor1] = useState(existing?.color1 || "#FF8E53");
  const [coloredText1, setColoredText1] = useState(
    existing?.coloredText1 || "",
  );
  const [text2, setText2] = useState(existing?.text2 || "");
  const [text3, setText3] = useState(existing?.text3 || "");
  const [text4, setText4] = useState(existing?.text4 || "");
  const [text5, setText5] = useState(existing?.text5 || "");
  const [text6, setText6] = useState(existing?.text6 || "");
  const [text8, setText8] = useState(existing?.text8 || "");

  // Template 3 — 4-item grid
  const [t3Items, setT3Items] = useState(() =>
    [1, 2, 3, 4].map((n) => ({
      imageFile: null,
      imagePreview: existing?.[`image${n}Url`] || "",
      imageUrl: existing?.[`image${n}Url`] || "",
      imagePath: existing?.[`image${n}Path`] || "",
      label: existing?.[`label${n}`] || "",
      para: existing?.[`para${n}`] || "",
      color: existing?.[`color${n}`] || "#FF8E53",
      numColor: existing?.[`numColor${n}`] || "#ffffff",
    })),
  );
  const t3ImgRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  // Template 4 state
  const [t4img1File, setT4img1File] = useState(null);
  const [t4img1Preview, setT4img1Preview] = useState(existing?.t4img1Url || "");
  const [t4img2File, setT4img2File] = useState(null);
  const [t4img2Preview, setT4img2Preview] = useState(existing?.t4img2Url || "");
  const [t4img3File, setT4img3File] = useState(null);
  const [t4img3Preview, setT4img3Preview] = useState(existing?.t4img3Url || "");
  const [t4text1, setT4text1] = useState(existing?.t4text1 || "");
  const [t4text1Color, setT4text1Color] = useState(
    existing?.t4text1Color || "#FF8E53",
  );
  const [t4text2, setT4text2] = useState(existing?.t4text2 || "");
  const [t4text3, setT4text3] = useState(existing?.t4text3 || "");
  const [t4text4, setT4text4] = useState(existing?.t4text4 || "");
  const [t4text5, setT4text5] = useState(existing?.t4text5 || "");
  const [t4text6, setT4text6] = useState(existing?.t4text6 || "");
  const [t4setCount, setT4setCount] = useState(existing?.t4setCount || 1);
  const [t4sets, setT4sets] = useState(() =>
    [1, 2, 3].map((n) => ({
      title: existing?.[`t4set${n}Title`] || "",
      para: existing?.[`t4set${n}Para`] || "",
    })),
  );
  const t4img1Ref = useRef(null);
  const t4img2Ref = useRef(null);
  const t4img3Ref = useRef(null);

  // Template 5 state
  const [t5img1File, setT5img1File] = useState(null);
  const [t5img1Preview, setT5img1Preview] = useState(existing?.t5img1Url || "");
  const [t5img2File, setT5img2File] = useState(null);
  const [t5img2Preview, setT5img2Preview] = useState(existing?.t5img2Url || "");
  const [t5img3File, setT5img3File] = useState(null);
  const [t5img3Preview, setT5img3Preview] = useState(existing?.t5img3Url || "");
  const [t5coloredText1, setT5coloredText1] = useState(
    existing?.t5coloredText1 || "",
  );
  const [t5coloredText1Color, setT5coloredText1Color] = useState(
    existing?.t5coloredText1Color || "#e91e63",
  );
  const [t5text1, setT5text1] = useState(existing?.t5text1 || "");
  const [t5text2, setT5text2] = useState(existing?.t5text2 || "");
  const [t5text3, setT5text3] = useState(existing?.t5text3 || "");
  const [t5text4, setT5text4] = useState(existing?.t5text4 || "");
  const [t5highlightColor, setT5highlightColor] = useState(
    existing?.t5highlightColor || "#fce138",
  );
  const [t5setCount, setT5setCount] = useState(existing?.t5setCount || 3);
  const [t5sets, setT5sets] = useState(() =>
    [1, 2, 3].map((n) => ({
      text5: existing?.[`t5set${n}Text5`] || "",
      text6: existing?.[`t5set${n}Text6`] || "",
    })),
  );
  const [t5text7, setT5text7] = useState(existing?.t5text7 || "");
  const [t5text8, setT5text8] = useState(existing?.t5text8 || "");
  const [t5text9, setT5text9] = useState(existing?.t5text9 || "");
  const [t5circleTitle, setT5circleTitle] = useState(
    existing?.t5circleTitle || "",
  );
  const [t5circleBody, setT5circleBody] = useState(
    existing?.t5circleBody || "",
  );
  const [t5circleColor, setT5circleColor] = useState(
    existing?.t5circleColor || "#fce138",
  );
  const t5img1Ref = useRef(null);
  const t5img2Ref = useRef(null);
  const t5img3Ref = useRef(null);
  const updateT5Set = (i, field, val) =>
    setT5sets((prev) =>
      prev.map((s, idx) => (idx === i ? { ...s, [field]: val } : s)),
    );

  // Template 6 state
  const [t6imgCount, setT6imgCount] = useState(existing?.t6imgCount || 1);
  const [t6layout, setT6layout] = useState(existing?.t6layout || "h");
  const [t6imgs, setT6imgs] = useState(() =>
    Array.from({ length: 8 }, (_, i) => ({
      file: null,
      preview: existing?.[`t6img${i + 1}Url`] || "",
      url: existing?.[`t6img${i + 1}Url`] || "",
      path: existing?.[`t6img${i + 1}Path`] || "",
    })),
  );
  const t6fileRefs = useRef([]);
  const updateT6Img = (i, file) => {
    setT6imgs((prev) =>
      prev.map((img, idx) =>
        idx === i ? { ...img, file, preview: URL.createObjectURL(file) } : img,
      ),
    );
  };

  const handleT4Image = (e, setter, previewSetter) => {
    const file = e.target.files[0];
    if (!file) return;
    setter(file);
    previewSetter(URL.createObjectURL(file));
  };

  const updateT4Set = (i, field, val) =>
    setT4sets((prev) =>
      prev.map((s, idx) => (idx === i ? { ...s, [field]: val } : s)),
    );

  const updateT3Item = (i, field, val) =>
    setT3Items((prev) =>
      prev.map((it, idx) => (idx === i ? { ...it, [field]: val } : it)),
    );

  const handleT3Image = (e, i) => {
    const file = e.target.files[0];
    if (!file) return;
    updateT3Item(i, "imageFile", file);
    updateT3Item(i, "imagePreview", URL.createObjectURL(file));
  };

  const [saving, setSaving] = useState(false);
  const imgRef = useRef(null);

  const handleImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const save = async () => {
    setSaving(true);

    if (templateType === "template6") {
      const uploadT6 = async (img) => {
        if (img.file) {
          if (img.path && existing) await deletePhoto(img.path);
          return await uploadPhoto(img.file, `tripEntries/${tripId}`);
        }
        return { url: img.url || "", path: img.path || "" };
      };
      const results = await Promise.all(
        t6imgs.slice(0, t6imgCount).map((img) => uploadT6(img)),
      );
      const payload = { date, entryType: "template6", t6imgCount, t6layout };
      results.forEach((r, i) => {
        payload[`t6img${i + 1}Url`] = r.url;
        payload[`t6img${i + 1}Path`] = r.path;
      });
      if (existing) {
        await updateDoc(
          doc(db, "trips", tripId, "dayEntries", existing.id),
          payload,
        );
      } else {
        await addDoc(collection(db, "trips", tripId, "dayEntries"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      setSaving(false);
      onDone();
      return;
    }

    if (templateType === "template5") {
      const uploadT5 = async (file, existingUrl, existingPath) => {
        if (file) {
          if (existingPath && existing) await deletePhoto(existingPath);
          return await uploadPhoto(file, `tripEntries/${tripId}`);
        }
        return { url: existingUrl || "", path: existingPath || "" };
      };
      const [r1, r2, r3] = await Promise.all([
        uploadT5(t5img1File, existing?.t5img1Url, existing?.t5img1Path),
        uploadT5(t5img2File, existing?.t5img2Url, existing?.t5img2Path),
        uploadT5(t5img3File, existing?.t5img3Url, existing?.t5img3Path),
      ]);
      const payload = {
        date,
        entryType: "template5",
        t5img1Url: r1.url,
        t5img1Path: r1.path,
        t5img2Url: r2.url,
        t5img2Path: r2.path,
        t5img3Url: r3.url,
        t5img3Path: r3.path,
        t5coloredText1,
        t5coloredText1Color,
        t5text1,
        t5text2,
        t5text3,
        t5text4,
        t5highlightColor,
        t5setCount,
        t5set1Text5: t5sets[0].text5,
        t5set1Text6: t5sets[0].text6,
        t5set2Text5: t5sets[1].text5,
        t5set2Text6: t5sets[1].text6,
        t5set3Text5: t5sets[2].text5,
        t5set3Text6: t5sets[2].text6,
        t5text7,
        t5text8,
        t5text9,
        t5circleTitle,
        t5circleBody,
        t5circleColor,
      };
      if (existing) {
        await updateDoc(
          doc(db, "trips", tripId, "dayEntries", existing.id),
          payload,
        );
      } else {
        await addDoc(collection(db, "trips", tripId, "dayEntries"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      setSaving(false);
      onDone();
      return;
    }

    if (templateType === "template4") {
      const uploadT4 = async (file, existingUrl, existingPath) => {
        if (file) {
          if (existingPath && existing) await deletePhoto(existingPath);
          return await uploadPhoto(file, `tripEntries/${tripId}`);
        }
        return { url: existingUrl || "", path: existingPath || "" };
      };
      const [r1, r2, r3] = await Promise.all([
        uploadT4(t4img1File, existing?.t4img1Url, existing?.t4img1Path),
        uploadT4(t4img2File, existing?.t4img2Url, existing?.t4img2Path),
        uploadT4(t4img3File, existing?.t4img3Url, existing?.t4img3Path),
      ]);
      const payload = {
        date,
        entryType: "template4",
        t4img1Url: r1.url,
        t4img1Path: r1.path,
        t4img2Url: r2.url,
        t4img2Path: r2.path,
        t4img3Url: r3.url,
        t4img3Path: r3.path,
        t4text1,
        t4text1Color,
        t4text2,
        t4text3,
        t4text4,
        t4text5,
        t4text6,
        t4setCount,
        t4set1Title: t4sets[0].title,
        t4set1Para: t4sets[0].para,
        t4set2Title: t4sets[1].title,
        t4set2Para: t4sets[1].para,
        t4set3Title: t4sets[2].title,
        t4set3Para: t4sets[2].para,
      };
      if (existing) {
        await updateDoc(
          doc(db, "trips", tripId, "dayEntries", existing.id),
          payload,
        );
      } else {
        await addDoc(collection(db, "trips", tripId, "dayEntries"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      setSaving(false);
      onDone();
      return;
    }

    if (templateType === "template3") {
      const resolvedItems = await Promise.all(
        t3Items.map(async (item) => {
          let { imageUrl, imagePath } = item;
          if (item.imageFile) {
            if (imagePath && existing) await deletePhoto(imagePath);
            const res = await uploadPhoto(
              item.imageFile,
              `tripEntries/${tripId}`,
            );
            imageUrl = res.url;
            imagePath = res.path;
          }
          return {
            imageUrl,
            imagePath,
            label: item.label,
            para: item.para,
            color: item.color,
            numColor: item.numColor,
          };
        }),
      );
      const payload = { date, entryType: "template3" };
      resolvedItems.forEach((it, i) => {
        payload[`image${i + 1}Url`] = it.imageUrl;
        payload[`image${i + 1}Path`] = it.imagePath;
        payload[`label${i + 1}`] = it.label;
        payload[`para${i + 1}`] = it.para;
        payload[`color${i + 1}`] = it.color;
        payload[`numColor${i + 1}`] = it.numColor;
      });
      if (existing) {
        await updateDoc(
          doc(db, "trips", tripId, "dayEntries", existing.id),
          payload,
        );
      } else {
        await addDoc(collection(db, "trips", tripId, "dayEntries"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
      }
      setSaving(false);
      onDone();
      return;
    }

    // Template 1 / 2
    let imageUrl = existing?.imageUrl || "";
    let imagePath = existing?.imagePath || "";
    if (imageFile) {
      if (existing?.imagePath) await deletePhoto(existing.imagePath);
      const res = await uploadPhoto(imageFile, `tripEntries/${tripId}`);
      imageUrl = res.url;
      imagePath = res.path;
    }
    const payload = {
      date,
      entryType: templateType,
      imageUrl,
      imagePath,
      color1,
      coloredText1,
      textColor,
      text2,
      text3,
      text4,
      text5,
      text6,
      text8,
    };
    if (existing) {
      await updateDoc(
        doc(db, "trips", tripId, "dayEntries", existing.id),
        payload,
      );
    } else {
      await addDoc(collection(db, "trips", tripId, "dayEntries"), {
        ...payload,
        createdAt: serverTimestamp(),
      });
    }
    setSaving(false);
    onDone();
  };

  const canSave =
    templateType === "template3"
      ? t3Items.every((it) => it.imagePreview)
      : templateType === "template4"
        ? t4img1Preview && t4img3Preview
        : templateType === "template5"
          ? !!(t5img1Preview || t5text1 || t5sets[0].text5)
          : templateType === "template6"
            ? t6imgs.slice(0, t6imgCount).some((img) => img.preview)
            : imageFile || imagePreview;

  const ColorPalette = () => (
    <div className="atf-palette">
      {TEMPLATE_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          className={`atf-swatch ${color1 === c ? "active" : ""}`}
          style={{ background: c }}
          onClick={() => setColor1(c)}
        />
      ))}
    </div>
  );

  return (
    <div className="add-template-form">
      {!existing && (
        <div className="atf-type-picker">
          <button
            type="button"
            className={`atf-type-card ${templateType === "template1" ? "active" : ""}`}
            onClick={() => setTemplateType("template1")}
          >
            <div className="atf-type-preview atf-type-preview--1">
              <div className="atp-strip" />
              <div className="atp-img" />
            </div>
            <span>Side image</span>
          </button>
          <button
            type="button"
            className={`atf-type-card ${templateType === "template2" ? "active" : ""}`}
            onClick={() => setTemplateType("template2")}
          >
            <div className="atf-type-preview atf-type-preview--2">
              <div className="atp-overlay" />
              <div className="atp-text-block" />
            </div>
            <span>Full bleed</span>
          </button>
          <button
            type="button"
            className={`atf-type-card ${templateType === "template3" ? "active" : ""}`}
            onClick={() => setTemplateType("template3")}
          >
            <div className="atf-type-preview atf-type-preview--3">
              <div className="atp-g1" />
              <div className="atp-g2" />
              <div className="atp-g3" />
              <div className="atp-g4" />
            </div>
            <span>4-grid</span>
          </button>
          <button
            type="button"
            className={`atf-type-card ${templateType === "template4" ? "active" : ""}`}
            onClick={() => setTemplateType("template4")}
          >
            <div className="atf-type-preview atf-type-preview--4">
              <div className="atp-t4-left" />
              <div className="atp-t4-right">
                <div className="atp-t4-r1" />
                <div className="atp-t4-r2" />
              </div>
            </div>
            <span>Magazine</span>
          </button>
          <button
            type="button"
            className={`atf-type-card ${templateType === "template5" ? "active" : ""}`}
            onClick={() => setTemplateType("template5")}
          >
            <div className="atf-type-preview atf-type-preview--5">
              <div className="atp-t5-left">
                <div className="atp-t5-img" />
                <div className="atp-t5-text" />
              </div>
              <div className="atp-t5-right">
                <div className="atp-t5-r1" />
                <div className="atp-t5-r2" />
                <div className="atp-t5-r3" />
              </div>
            </div>
            <span>Article</span>
          </button>
          <button
            type="button"
            className={`atf-type-card ${templateType === "template6" ? "active" : ""}`}
            onClick={() => setTemplateType("template6")}
          >
            <div className="atf-type-preview atf-type-preview--6">
              <div className="atp-t6-a" />
              <div className="atp-t6-b" />
              <div className="atp-t6-c" />
              <div className="atp-t6-d" />
            </div>
            <span>Collage</span>
          </button>
        </div>
      )}

      {allDays.length > 1 && (
        <select
          className="aef-day"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        >
          {allDays.map((d, i) => (
            <option key={d} value={d}>
              {fmtDay(d, i)}
            </option>
          ))}
        </select>
      )}

      {templateType === "template5" ? (
        <>
          {/* image1 full-width, then image2 + image3 side by side */}
          <div
            className="atf-image-slot"
            onClick={() => t5img1Ref.current?.click()}
          >
            {t5img1Preview ? (
              <img src={t5img1Preview} alt="" className="atf-image-preview" />
            ) : (
              <span>📷 Image 1 — large hero</span>
            )}
          </div>
          <div className="atf-t5-thumbs">
            <div
              className="atf-image-slot atf-image-slot--sm"
              onClick={() => t5img2Ref.current?.click()}
            >
              {t5img2Preview ? (
                <img src={t5img2Preview} alt="" className="atf-image-preview" />
              ) : (
                <span>📷 Image 2 — body</span>
              )}
            </div>
            <div
              className="atf-image-slot atf-image-slot--sm"
              onClick={() => t5img3Ref.current?.click()}
            >
              {t5img3Preview ? (
                <img src={t5img3Preview} alt="" className="atf-image-preview" />
              ) : (
                <span>📷 Image 3 — portrait</span>
              )}
            </div>
          </div>
          <input
            ref={t5img1Ref}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => handleT4Image(e, setT5img1File, setT5img1Preview)}
          />
          <input
            ref={t5img2Ref}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => handleT4Image(e, setT5img2File, setT5img2Preview)}
          />
          <input
            ref={t5img3Ref}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => handleT4Image(e, setT5img3File, setT5img3Preview)}
          />

          {/* Left column text */}
          <div className="atf-palette-row">
            <div className="atf-palette">
              {TEMPLATE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`atf-swatch atf-swatch--xs ${t5coloredText1Color === c ? "active" : ""}`}
                  style={{ background: c }}
                  onClick={() => setT5coloredText1Color(c)}
                />
              ))}
            </div>
            <input
              className="atf-input"
              placeholder="Colored label (e.g. A taste of)"
              maxLength={30}
              value={t5coloredText1}
              style={{ color: t5coloredText1Color }}
              onChange={(e) => setT5coloredText1(e.target.value)}
            />
          </div>
          <input
            className="atf-input atf-bold"
            placeholder="Title"
            maxLength={60}
            value={t5text1}
            onChange={(e) => setT5text1(e.target.value)}
          />
          <input
            className="atf-input"
            placeholder="Subtitle (uppercase tagline)"
            maxLength={80}
            value={t5text2}
            onChange={(e) => setT5text2(e.target.value)}
          />
          <textarea
            className="atf-textarea"
            placeholder="Body paragraph…"
            maxLength={600}
            rows={3}
            value={t5text3}
            onChange={(e) => setT5text3(e.target.value)}
          />

          {/* Right column */}
          <input
            className="atf-input atf-bold"
            placeholder="Section header (right column)"
            maxLength={40}
            value={t5text4}
            onChange={(e) => setT5text4(e.target.value)}
          />

          <div className="atf-palette-row">
            <div className="atf-palette">
              {TEMPLATE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`atf-swatch atf-swatch--xs ${t5highlightColor === c ? "active" : ""}`}
                  style={{ background: c }}
                  onClick={() => setT5highlightColor(c)}
                />
              ))}
            </div>
            <span className="atf-text-color-label">Highlight colour</span>
          </div>

          <div className="atf-t4-set-count">
            <span className="atf-text-color-label">Sets</span>
            {[1, 2, 3].map((n) => (
              <button
                key={n}
                type="button"
                className={`atf-set-count-btn ${t5setCount === n ? "active" : ""}`}
                onClick={() => setT5setCount(n)}
              >
                {n}
              </button>
            ))}
          </div>

          {Array.from({ length: t5setCount }, (_, i) => (
            <div key={i} className="atf-t4-set">
              <input
                className="atf-input atf-bold"
                placeholder={`Highlighted label ${i + 1}`}
                maxLength={40}
                value={t5sets[i].text5}
                style={{ color: t5highlightColor }}
                onChange={(e) => updateT5Set(i, "text5", e.target.value)}
              />
              <textarea
                className="atf-textarea"
                placeholder="Content paragraph…"
                maxLength={300}
                rows={2}
                value={t5sets[i].text6}
                onChange={(e) => updateT5Set(i, "text6", e.target.value)}
              />
            </div>
          ))}

          {/* Callout block */}
          <input
            className="atf-input atf-bold"
            placeholder="Callout title (e.g. The ingredient)"
            maxLength={40}
            value={t5text7}
            onChange={(e) => setT5text7(e.target.value)}
          />
          <textarea
            className="atf-textarea"
            placeholder="Callout body text…"
            maxLength={400}
            rows={3}
            value={t5text8}
            onChange={(e) => setT5text8(e.target.value)}
          />

          {/* Caption below thumbnail */}
          <input
            className="atf-input"
            placeholder="Image caption (e.g. Left: Baja-style fish tacos…)"
            maxLength={80}
            value={t5text9}
            onChange={(e) => setT5text9(e.target.value)}
          />

          {/* Circle badge */}
          <div className="atf-palette-row" style={{ marginTop: "0.4rem" }}>
            <span className="atf-text-color-label">Circle badge</span>
            <div className="atf-palette">
              {TEMPLATE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`atf-swatch atf-swatch--xs ${t5circleColor === c ? "active" : ""}`}
                  style={{ background: c }}
                  onClick={() => setT5circleColor(c)}
                />
              ))}
            </div>
          </div>
          <input
            className="atf-input atf-bold"
            placeholder="Circle title"
            maxLength={30}
            value={t5circleTitle}
            onChange={(e) => setT5circleTitle(e.target.value)}
          />
          <textarea
            className="atf-textarea"
            placeholder="Circle body text…"
            maxLength={100}
            rows={2}
            value={t5circleBody}
            onChange={(e) => setT5circleBody(e.target.value)}
          />
        </>
      ) : templateType === "template6" ? (
        <>
          {/* Image count selector */}
          <div className="atf-t4-set-count">
            <span className="atf-text-color-label">Images</span>
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <button
                key={n}
                type="button"
                className={`atf-set-count-btn ${t6imgCount === n ? "active" : ""}`}
                onClick={() => setT6imgCount(n)}
              >
                {n}
              </button>
            ))}
          </div>

          {/* Layout toggle for 2 images */}
          {t6imgCount === 2 && (
            <div className="atf-t4-set-count">
              <span className="atf-text-color-label">Layout</span>
              <button
                type="button"
                className={`atf-layout-btn ${t6layout === "h" ? "active" : ""}`}
                onClick={() => setT6layout("h")}
                title="Side by side"
              >
                <span className="atf-layout-icon atf-layout-icon--h">
                  <span /><span />
                </span>
              </button>
              <button
                type="button"
                className={`atf-layout-btn ${t6layout === "v" ? "active" : ""}`}
                onClick={() => setT6layout("v")}
                title="Stacked"
              >
                <span className="atf-layout-icon atf-layout-icon--v">
                  <span /><span />
                </span>
              </button>
            </div>
          )}

          {/* Image grid */}
          <div className="atf-t6-grid" data-count={t6imgCount}>
            {Array.from({ length: t6imgCount }, (_, i) => (
              <div
                key={i}
                className="atf-image-slot atf-image-slot--t6"
                onClick={() => t6fileRefs.current[i]?.click()}
              >
                {t6imgs[i].preview ? (
                  <img src={t6imgs[i].preview} alt="" className="atf-image-preview" />
                ) : (
                  <span>📷 {i + 1}</span>
                )}
              </div>
            ))}
          </div>

          {Array.from({ length: 8 }, (_, i) => (
            <input
              key={i}
              ref={(el) => { t6fileRefs.current[i] = el; }}
              type="file"
              accept="image/*"
              hidden
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) updateT6Img(i, file);
              }}
            />
          ))}
        </>
      ) : templateType === "template4" ? (
        <>
          {/* 3-image row: image1 tall left, image2+image3 stacked right */}
          <div className="atf-t4-images">
            <div
              className="atf-t4-img-left"
              onClick={() => t4img1Ref.current?.click()}
            >
              {t4img1Preview ? (
                <img src={t4img1Preview} alt="" className="atf-image-preview" />
              ) : (
                <span>📷 Main</span>
              )}
            </div>
            <div className="atf-t4-img-right">
              <div
                className="atf-image-slot"
                onClick={() => t4img2Ref.current?.click()}
              >
                {t4img2Preview ? (
                  <img
                    src={t4img2Preview}
                    alt=""
                    className="atf-image-preview"
                  />
                ) : (
                  <span>📷 2</span>
                )}
              </div>
              <div
                className="atf-image-slot"
                onClick={() => t4img3Ref.current?.click()}
              >
                {t4img3Preview ? (
                  <img
                    src={t4img3Preview}
                    alt=""
                    className="atf-image-preview"
                  />
                ) : (
                  <span>📷 3</span>
                )}
              </div>
            </div>
          </div>
          <input
            ref={t4img1Ref}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => handleT4Image(e, setT4img1File, setT4img1Preview)}
          />
          <input
            ref={t4img2Ref}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => handleT4Image(e, setT4img2File, setT4img2Preview)}
          />
          <input
            ref={t4img3Ref}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => handleT4Image(e, setT4img3File, setT4img3Preview)}
          />

          {/* Left column text fields */}
          <div className="atf-palette-row">
            <div className="atf-palette">
              {TEMPLATE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`atf-swatch atf-swatch--xs ${t4text1Color === c ? "active" : ""}`}
                  style={{ background: c }}
                  onClick={() => setT4text1Color(c)}
                />
              ))}
            </div>
            <input
              className="atf-input"
              placeholder="Highlight label"
              maxLength={30}
              value={t4text1}
              style={{ color: t4text1Color }}
              onChange={(e) => setT4text1(e.target.value)}
            />
          </div>
          <input
            className="atf-input atf-bold"
            placeholder="Title"
            maxLength={60}
            value={t4text2}
            onChange={(e) => setT4text2(e.target.value)}
          />
          <input
            className="atf-input"
            placeholder="Subtitle"
            maxLength={60}
            value={t4text3}
            onChange={(e) => setT4text3(e.target.value)}
          />
          <textarea
            className="atf-textarea"
            placeholder="Body paragraph (2 columns)…"
            maxLength={600}
            rows={3}
            value={t4text4}
            onChange={(e) => setT4text4(e.target.value)}
          />
          <input
            className="atf-input"
            placeholder="Caption over image 2"
            maxLength={60}
            value={t4text5}
            onChange={(e) => setT4text5(e.target.value)}
          />

          {/* Right column text fields */}
          <input
            className="atf-input atf-bold"
            placeholder="Section header (right column)"
            maxLength={50}
            value={t4text6}
            onChange={(e) => setT4text6(e.target.value)}
          />

          {/* Set count toggle */}
          <div className="atf-t4-set-count">
            <span className="atf-text-color-label">Sets</span>
            {[1, 2, 3].map((n) => (
              <button
                key={n}
                type="button"
                className={`atf-set-count-btn ${t4setCount === n ? "active" : ""}`}
                onClick={() => setT4setCount(n)}
              >
                {n}
              </button>
            ))}
          </div>

          {/* Per-set inputs */}
          {Array.from({ length: t4setCount }, (_, i) => (
            <div key={i} className="atf-t4-set">
              <input
                className="atf-input atf-bold"
                placeholder={`Set ${i + 1} title`}
                maxLength={50}
                value={t4sets[i].title}
                onChange={(e) => updateT4Set(i, "title", e.target.value)}
              />
              <textarea
                className="atf-textarea"
                placeholder="Mini paragraph…"
                maxLength={300}
                rows={2}
                value={t4sets[i].para}
                onChange={(e) => updateT4Set(i, "para", e.target.value)}
              />
            </div>
          ))}
        </>
      ) : templateType === "template3" ? (
        <>
          {t3Items.map((item, i) => (
            <div key={i} className="atf-t3-item">
              <div className="atf-t3-item-top">
                <span className="atf-t3-num">{i + 1}</span>
                <div
                  className="atf-image-slot atf-image-slot--sm"
                  onClick={() => t3ImgRefs[i].current?.click()}
                >
                  {item.imagePreview ? (
                    <img
                      src={item.imagePreview}
                      alt=""
                      className="atf-image-preview"
                    />
                  ) : (
                    <span>📷</span>
                  )}
                </div>
                <input
                  ref={t3ImgRefs[i]}
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => handleT3Image(e, i)}
                />
              </div>
              <div className="atf-t3-color-row">
                {TEMPLATE_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    className={`atf-swatch atf-swatch--xs ${item.color === c ? "active" : ""}`}
                    style={{ background: c }}
                    onClick={() => updateT3Item(i, "color", c)}
                  />
                ))}
              </div>
              <div className="atf-text-color-row">
                <span className="atf-text-color-label">№</span>
                <button
                  type="button"
                  className={`atf-text-color-btn ${item.numColor === "#ffffff" ? "active" : ""}`}
                  style={{ background: "#ffffff" }}
                  onClick={() => updateT3Item(i, "numColor", "#ffffff")}
                  title="White number"
                >
                  A
                </button>
                <button
                  type="button"
                  className={`atf-text-color-btn ${item.numColor === "#1a0f08" ? "active" : ""}`}
                  style={{ background: "#1a0f08", color: "#ffffff" }}
                  onClick={() => updateT3Item(i, "numColor", "#1a0f08")}
                  title="Dark number"
                >
                  A
                </button>
              </div>
              <input
                className="atf-input"
                placeholder="Colored label"
                maxLength={30}
                value={item.label}
                style={{ color: item.color }}
                onChange={(e) => updateT3Item(i, "label", e.target.value)}
              />
              <textarea
                className="atf-textarea"
                placeholder="Paragraph…"
                maxLength={300}
                rows={2}
                value={item.para}
                onChange={(e) => updateT3Item(i, "para", e.target.value)}
              />
            </div>
          ))}
        </>
      ) : (
        <>
          <div
            className="atf-image-slot"
            onClick={() => imgRef.current?.click()}
          >
            {imagePreview ? (
              <img src={imagePreview} alt="" className="atf-image-preview" />
            ) : (
              <span>📷 Tap to add page image</span>
            )}
          </div>
          <input
            ref={imgRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleImage}
          />

          <div className="atf-palette-row">
            <ColorPalette />
            <input
              className="atf-input"
              placeholder="Colored text"
              maxLength={20}
              value={coloredText1}
              style={{ color: color1 }}
              onChange={(e) => setColoredText1(e.target.value)}
            />
          </div>

          <div className="atf-text-color-row">
            <span className="atf-text-color-label">Text</span>
            <button
              type="button"
              className={`atf-text-color-btn ${textColor === "#ffffff" ? "active" : ""}`}
              style={{ background: "#ffffff" }}
              onClick={() => setTextColor("#ffffff")}
              title="White text"
            >
              A
            </button>
            <button
              type="button"
              className={`atf-text-color-btn ${textColor === "#1a0f08" ? "active" : ""}`}
              style={{ background: "#1a0f08", color: "#ffffff" }}
              onClick={() => setTextColor("#1a0f08")}
              title="Dark text"
            >
              A
            </button>
          </div>

          <input
            className="atf-input atf-bold"
            placeholder="Title"
            maxLength={50}
            value={text2}
            onChange={(e) => setText2(e.target.value)}
          />
          <textarea
            className="atf-textarea"
            placeholder="Paragraph…"
            maxLength={500}
            rows={4}
            value={text3}
            onChange={(e) => setText3(e.target.value)}
          />

          <div className="atf-row">
            <input
              className="atf-input"
              placeholder="Text 1"
              maxLength={20}
              value={text4}
              onChange={(e) => setText4(e.target.value)}
            />
            <input
              className="atf-input"
              placeholder="Text 2"
              maxLength={20}
              value={text5}
              onChange={(e) => setText5(e.target.value)}
            />
          </div>

          <input
            className="atf-input"
            placeholder="Text 3"
            maxLength={40}
            value={text6}
            onChange={(e) => setText6(e.target.value)}
          />
          <input
            className="atf-input"
            placeholder="Text 4"
            maxLength={20}
            value={text8}
            onChange={(e) => setText8(e.target.value)}
          />
        </>
      )}

      <button className="aef-save" onClick={save} disabled={saving || !canSave}>
        {saving
          ? "Saving…"
          : existing
            ? "Save changes ♥"
            : "Add template page ♥"}
      </button>
    </div>
  );
};

// ── Journal page ──────────────────────────────────────────────────────────────

const JournalPage = forwardRef(
  (
    { date, slotEntries, slotIdx, tripId, onPhotoClick, pageNum, editMode },
    ref,
  ) => (
    <div className="journal-page" ref={ref}>
      <div className="page-inner">
        <div className="page-watermark">{pageNum}</div>
        <div
          className={`page-day-header ${slotIdx > 0 ? "page-day-header--cont" : ""}`}
        >
          {slotIdx === 0
            ? fmtDateHeader(date)
            : `${fmtDateHeader(date)} (cont'd)`}
        </div>
        <div className="entries-list">
          {slotEntries.map((entry) => (
            <EntryBlock
              key={entry.id}
              entry={entry}
              tripId={tripId}
              onPhotoClick={onPhotoClick}
              editMode={editMode}
            />
          ))}
        </div>
      </div>
    </div>
  ),
);
JournalPage.displayName = "JournalPage";

// ── Cover ─────────────────────────────────────────────────────────────────────

const CoverPage = forwardRef(({ trip, isFront }, ref) => (
  <div
    className={`journal-page journal-cover ${isFront ? "cover-front" : "cover-back"}`}
    ref={ref}
  >
    {isFront && trip.coverPhotoUrl && (
      <div
        className="cover-photo-bg"
        style={{ backgroundImage: `url("${trip.coverPhotoUrl}")` }}
      />
    )}
    <div className="cover-inner">
      {isFront ? (
        <>
          <div className="cover-title">{trip.title}</div>
          <div className="cover-dest">{trip.destination}</div>
          <div className="cover-dates">
            {trip.startDate} – {trip.endDate ?? trip.startDate}
          </div>
          <div className="cover-deco">✈ ♥ ✦</div>
        </>
      ) : (
        <div className="cover-back-text">fin ♥</div>
      )}
    </div>
  </div>
));
CoverPage.displayName = "CoverPage";

// ── Main TripBook ─────────────────────────────────────────────────────────────

const TripBook = ({ trip, onClose }) => {
  const allDays = getDays(trip.startDate, trip.endDate);

  const [entries, setEntries] = useState([]);
  const [lightbox, setLightbox] = useState(null);
  const [pageNum, setPageNum] = useState(0);
  const [addingFor, setAddingFor] = useState(null);
  const [addingTemplate, setAddingTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [pageBudget, setPageBudget] = useState(DEFAULT_PAGE_BUDGET);
  const bookRef = useRef(null);
  const wrapRef = useRef(null);

  // Derive usable page height from the rendered flipbook container
  useEffect(() => {
    if (!wrapRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      // size="stretch" scales the page maintaining 420:580 aspect ratio,
      // clamped to minHeight/maxHeight. Page height = min(container height, width/2 * 580/420).
      const pageH = Math.min(height, (width / 2) * (580 / 420));
      const clamped = Math.min(Math.max(pageH, 320), 780);
      // Subtract page-inner padding (~35px) + day-header (~33px)
      setPageBudget(Math.floor(clamped - 68));
    });
    observer.observe(wrapRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const q = query(
      collection(db, "trips", trip.id, "dayEntries"),
      orderBy("createdAt"),
    );
    return onSnapshot(q, (snap) =>
      setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    );
  }, [trip.id]);

  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        if (lightbox) setLightbox(null);
        else if (addingFor) setAddingFor(null);
        else onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox, addingFor, onClose]);

  const pageSlots = buildPageSlots(allDays, entries, pageBudget);
  const totalPages = 2 + pageSlots.length; // cover + slots + back cover
  const isFirst = pageNum === 0;
  const isLast = pageNum >= totalPages - 1;

  const currentSlot =
    pageNum > 0 && pageNum <= pageSlots.length
      ? pageSlots[pageNum - 1]
      : pageSlots[Math.min(Math.max(pageNum - 1, 0), pageSlots.length - 1)];
  const currentDay = currentSlot?.date ?? allDays[0];

  // Dots: one per unique day, navigate to first slot of that day
  const dayFirstPage = {}; // date → 1-based page index
  pageSlots.forEach((slot, i) => {
    if (dayFirstPage[slot.date] === undefined) dayFirstPage[slot.date] = i + 1;
  });
  const uniqueDays = Object.keys(dayFirstPage);

  const flipNext = () => bookRef.current?.pageFlip()?.flipNext();
  const flipPrev = () => bookRef.current?.pageFlip()?.flipPrev();

  // Header label
  const headerLabel = (() => {
    if (pageNum === 0) return <span className="day-date">Cover</span>;
    if (pageNum > pageSlots.length)
      return <span className="day-date">The End</span>;
    const slot = pageSlots[pageNum - 1];
    const dayIdx = allDays.indexOf(slot.date);
    const label = `Day ${dayIdx + 1}${!slot.isTemplate && slot.slotIdx > 0 ? ` (cont'd)` : ""}`;
    return (
      <span className="day-num">
        {label} · {pageNum}/{pageSlots.length}
      </span>
    );
  })();

  return (
    <div
      className="book-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="book-container">
        {/* Header */}
        <div className="book-header">
          <span className="book-trip-title">
            {trip.title} · {trip.destination}
          </span>
          <div className="book-day-nav">
            <button
              className="btn-day-nav"
              onClick={flipPrev}
              disabled={isFirst}
            >
              ‹
            </button>
            <div className="book-day-label">{headerLabel}</div>
            <button
              className="btn-day-nav"
              onClick={flipNext}
              disabled={isLast}
            >
              ›
            </button>
          </div>
          <div className="book-header-actions">
            <button
              className={`btn-edit-mode ${editMode ? "active" : ""}`}
              onClick={() => setEditMode((m) => !m)}
              title={editMode ? "Exit edit mode" : "Edit entries"}
            >
              {editMode ? "✓ done" : "✏️ edit"}
            </button>
            <button className="book-close" onClick={onClose}>
              ×
            </button>
          </div>
        </div>

        {/* Book */}
        <div className="book-flipbook-wrap" ref={wrapRef}>
          <HTMLFlipBook
            key={pageSlots.length}
            ref={bookRef}
            width={420}
            height={580}
            size="stretch"
            minWidth={220}
            maxWidth={560}
            minHeight={320}
            maxHeight={780}
            showCover={true}
            flippingTime={650}
            drawShadow={true}
            usePortrait={false}
            startPage={pageNum}
            disableFlipByClick={true}
            useMouseEvents={!editMode}
            showPageCorners={!editMode}
            onFlip={(e) => setPageNum(e.data)}
            className="html-flip-book"
            enableAudio={!editMode}
          >
            <CoverPage trip={trip} isFront={true} />
            {pageSlots.map((slot, i) =>
              slot.isTemplate ? (
                (() => {
                  const tProps = {
                    key: `${slot.date}-tmpl-${slot.templateData.id}`,
                    data: slot.templateData,
                    tripId: trip.id,
                    editMode,
                    onEdit: () => setEditingTemplate(slot.templateData),
                  };
                  if (slot.templateData.entryType === "template2")
                    return <Template2Page {...tProps} />;
                  if (slot.templateData.entryType === "template3")
                    return <Template3Page {...tProps} />;
                  if (slot.templateData.entryType === "template4")
                    return <Template4Page {...tProps} />;
                  if (slot.templateData.entryType === "template5")
                    return <Template5Page {...tProps} />;
                  if (slot.templateData.entryType === "template6")
                    return <Template6Page {...tProps} />;
                  return <Template1Page {...tProps} />;
                })()
              ) : (
                <JournalPage
                  key={`${slot.date}-${slot.slotIdx}`}
                  date={slot.date}
                  slotEntries={slot.slotEntries}
                  slotIdx={slot.slotIdx}
                  tripId={trip.id}
                  onPhotoClick={setLightbox}
                  pageNum={i + 1}
                  editMode={editMode}
                />
              ),
            )}
            <CoverPage trip={trip} isFront={false} />
          </HTMLFlipBook>
        </div>

        {/* Bottom bar: dots (per day) + add button */}
        <div className="book-bottom">
          {uniqueDays.length > 1 && (
            <div className="book-dots">
              {uniqueDays.map((date) => (
                <button
                  key={date}
                  className={`day-dot ${currentDay === date && pageNum > 0 && pageNum <= pageSlots.length ? "active" : ""}`}
                  onClick={() =>
                    bookRef.current?.pageFlip()?.turnToPage(dayFirstPage[date])
                  }
                />
              ))}
            </div>
          )}
          {pageNum > 0 && pageNum <= pageSlots.length && (
            <>
              <button
                className="btn-add-template-global"
                onClick={() => setAddingTemplate(currentDay)}
                title="Add template page"
              >
                ⊞
              </button>
              <button
                className="btn-add-entry-global"
                onClick={() => setAddingFor(currentDay)}
                title="Add entry to this page"
              >
                ＋
              </button>
            </>
          )}
        </div>
      </div>

      {/* Add-entry popup */}
      {addingFor && (
        <div className="entry-popup-overlay">
          <div className="entry-popup">
            <div className="entry-popup-header">
              <span>New entry</span>
              <button onClick={() => setAddingFor(null)}>×</button>
            </div>
            <AddEntryForm
              tripId={trip.id}
              date={addingFor}
              allDays={allDays}
              onDone={() => setAddingFor(null)}
            />
          </div>
        </div>
      )}

      {addingTemplate !== null && (
        <div className="entry-popup-overlay">
          <div className="entry-popup entry-popup--wide">
            <div className="entry-popup-header">
              <span>Template page</span>
              <button onClick={() => setAddingTemplate(null)}>×</button>
            </div>
            <AddTemplate1Form
              tripId={trip.id}
              allDays={allDays}
              defaultDate={addingTemplate}
              onDone={() => setAddingTemplate(null)}
            />
          </div>
        </div>
      )}

      {/* Edit template popup */}
      {editingTemplate && (
        <div className="entry-popup-overlay">
          <div className="entry-popup entry-popup--wide">
            <div className="entry-popup-header">
              <span>Edit template page</span>
              <button onClick={() => setEditingTemplate(null)}>×</button>
            </div>
            <AddTemplate1Form
              tripId={trip.id}
              allDays={allDays}
              existing={editingTemplate}
              onDone={() => setEditingTemplate(null)}
            />
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="lightbox" onClick={() => setLightbox(null)}>
          <button className="lightbox-close" onClick={() => setLightbox(null)}>
            ×
          </button>
          <img src={lightbox.url} alt="" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
};

export default TripBook;
